import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { isPublicConsultaEnabled, isPublicDenunciaEnabled } from '../../utils/publicPages';
import { getAppHomePath } from '../../constants/navigation';
import {
  PoweredByUseAmbiental,
  TenantBanner,
  TenantCoatMark,
} from '../../components/Brand/TenantBrandMark';
import { tenantBrand } from '../../utils/tenantBranding';
import { isTenantSubdomain } from '../../utils/tenantProfile';
import {
  Lock,
  User,
  AlertCircle,
  Eye,
  EyeOff,
  Leaf,
  Shield,
  MapPin,
  ArrowRight,
  Sparkles,
} from 'lucide-react';

const REMEMBER_USER_KEY = 'ambiental_login_remember_username';

const Login = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [formData, setFormData] = useState({
    username: '',
    password: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [remember, setRemember] = useState(false);

  const { login, login2fa, loading } = useAuth();
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [totp, setTotp] = useState({ needed: false, tempToken: '', code: '' });

  useEffect(() => {
    try {
      const saved = localStorage.getItem(REMEMBER_USER_KEY);
      if (saved) {
        setFormData((prev) => ({ ...prev, username: saved }));
        setRemember(true);
      }
    } catch {
      /* ignore */
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = t('usernameRequired');
    }

    if (!formData.password) {
      newErrors.password = t('passwordRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      if (remember) {
        localStorage.setItem(REMEMBER_USER_KEY, formData.username.trim());
      } else {
        localStorage.removeItem(REMEMBER_USER_KEY);
      }
    } catch {
      /* ignore */
    }

    if (totp.needed) {
      if (!totp.code.trim()) {
        setErrors((prev) => ({ ...prev, totp: t('twoFactorRequired', { defaultValue: 'Informe o código do autenticador' }) }));
        return;
      }
      const verified = await login2fa(totp.tempToken, totp.code.trim());
      if (verified.success) {
        navigate(getAppHomePath(verified.user, tenant));
      }
      return;
    }

    const result = await login(formData);

    if (result.requires_2fa) {
      setTotp({ needed: true, tempToken: result.temp_token, code: '' });
      toast.success(t('twoFactorPrompt', { defaultValue: 'Digite o código do autenticador' }));
      return;
    }

    if (result.success) {
      navigate(getAppHomePath(result.user, tenant));
    }
  };

  const brand = tenantBrand(tenant);
  const orgName = tenant?.name || t('brandTitle');
  const allowOrgSignup = !isTenantSubdomain(
    typeof window !== 'undefined' ? window.location.hostname : '',
    process.env.REACT_APP_TENANT_BASE_DOMAIN || '',
  );
  const highlights = [
    { icon: Leaf, title: t('highlightLicensing'), text: t('highlightLicensingText') },
    { icon: MapPin, title: t('highlightMap'), text: t('highlightMapText') },
    { icon: Shield, title: t('highlightTenant'), text: t('highlightTenantText') },
  ];

  return (
    <div className="relative min-h-screen overflow-hidden bg-slate-950">
      {/* Fundo atmosférico */}
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_120%_80%_at_50%_-20%,rgba(46,125,50,0.35),transparent)]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-32 top-1/4 h-96 w-96 rounded-full bg-primary-700/25 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -right-24 bottom-0 h-80 w-80 rounded-full bg-emerald-600/20 blur-3xl"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07] [background-image:linear-gradient(rgba(255,255,255,0.15)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.15)_1px,transparent_1px)] [background-size:48px_48px]"
        aria-hidden
      />

      <div className="relative z-10 flex min-h-screen flex-col lg:flex-row">
        {/* Painel marca / ambiente — desktop */}
        <aside className="relative hidden overflow-hidden border-r border-white/10 bg-gradient-to-b from-primary-950/90 to-slate-950/95 lg:flex lg:w-[44%] lg:flex-col xl:w-[42%]">
          <div className="flex flex-1 flex-col justify-between px-10 py-10 xl:px-14">
          <div>
            <div className="mb-10">
              {brand.coatOfArmsUrl || brand.logoUrl ? (
                <TenantCoatMark
                  tenant={tenant}
                  size={220}
                  square={false}
                  className="max-w-[240px]"
                  fallback={false}
                />
              ) : (
                <div className="flex h-24 w-24 items-center justify-center rounded-full bg-primary-500/20 ring-1 ring-primary-400/30">
                  <Leaf className="h-12 w-12 text-primary-300" strokeWidth={1.75} aria-hidden />
                </div>
              )}
              <div className="mt-5">
                <PoweredByUseAmbiental light />
                <p className="mt-1 text-sm font-medium text-slate-300">{orgName}</p>
              </div>
            </div>

            <p className="max-w-md text-lg leading-relaxed text-slate-300">
              {t('heroLead')}
            </p>

            <ul className="mt-10 space-y-6">
              {highlights.map(({ icon: Icon, title, text }) => (
                <li key={title} className="flex gap-4">
                  <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10 text-primary-200 ring-1 ring-white/10">
                    <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </span>
                  <div>
                    <p className="font-semibold text-white">{title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-400">{text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <p className="mt-10 text-xs text-slate-500">{t('restrictedAccess')}</p>
          </div>
        </aside>

        {/* Formulário */}
        <main className="flex flex-1 flex-col justify-center px-4 py-10 sm:px-8 lg:px-12 xl:px-16">
          {/* Cabeçalho mobile */}
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            {brand.coatOfArmsUrl || brand.logoUrl ? (
              <TenantCoatMark tenant={tenant} size={88} square={false} fallback={false} />
            ) : (
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-600/90 shadow-lg shadow-primary-900/40">
                <Leaf className="h-6 w-6 text-white" strokeWidth={1.75} aria-hidden />
              </div>
            )}
            <div>
              <PoweredByUseAmbiental light />
              <p className="text-sm font-semibold text-white">{orgName}</p>
            </div>
          </div>

          <div className="mx-auto w-full max-w-[440px] animate-slide-up">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.97] shadow-strong backdrop-blur-sm">
              <div className="h-1 bg-gradient-to-r from-primary-600 via-emerald-600 to-primary-800" aria-hidden />
              {brand.bannerUrl ? (
                <div className="bg-white px-6 pt-6 sm:px-8">
                  <TenantBanner tenant={tenant} className="mx-auto h-20 w-full max-h-24" />
                </div>
              ) : null}

              <div className="px-6 pb-8 pt-7 sm:px-8 sm:pt-8">
                <div className="mb-8 flex items-start gap-3">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700 ring-1 ring-primary-100">
                    <Sparkles className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </span>
                  <div>
                    <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t('signIn')}</h2>
                    <p className="mt-1 text-sm text-gray-600">
                      {t('signInLead')}
                    </p>
                  </div>
                </div>

                <form className="space-y-5" onSubmit={handleSubmit} noValidate aria-busy={loading}>
                  <div className="form-group mb-0">
                    <label htmlFor="username" className="form-label">
                      {t('usernameOrEmail')}
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <User className="h-4 w-4" aria-hidden />
                      </span>
                      <input
                        id="username"
                        name="username"
                        type="text"
                        autoComplete="username"
                        value={formData.username}
                        onChange={handleChange}
                        className={`form-input pl-10 ${errors.username ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                        placeholder={t('usernamePlaceholder')}
                      />
                    </div>
                    {errors.username && (
                      <p className="form-error mt-1.5 flex items-center gap-1.5 animate-fade-in">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {errors.username}
                      </p>
                    )}
                  </div>

                  <div className="form-group mb-0">
                    <label htmlFor="password" className="form-label">
                      {t('common:password')}
                    </label>
                    <div className="relative">
                      <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 text-gray-400">
                        <Lock className="h-4 w-4" aria-hidden />
                      </span>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        value={formData.password}
                        onChange={handleChange}
                        className={`form-input pl-10 pr-11 ${errors.password ? 'border-danger-500 focus:ring-danger-500' : ''}`}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 transition-colors hover:text-primary-700"
                        onClick={() => setShowPassword(!showPassword)}
                        aria-label={showPassword ? t('common:hidePassword') : t('common:showPassword')}
                      >
                        {showPassword ? (
                          <EyeOff className="h-5 w-5" aria-hidden />
                        ) : (
                          <Eye className="h-5 w-5" aria-hidden />
                        )}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="form-error mt-1.5 flex items-center gap-1.5 animate-fade-in">
                        <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {errors.password}
                      </p>
                    )}
                  </div>

                  {totp.needed ? (
                    <div className="form-group mb-0">
                      <label htmlFor="totp" className="form-label">
                        {t('twoFactorCode', { defaultValue: 'Código do autenticador ou de recuperação' })}
                      </label>
                      <input
                        id="totp"
                        name="totp"
                        autoComplete="one-time-code"
                        value={totp.code}
                        onChange={(e) => setTotp((prev) => ({ ...prev, code: e.target.value }))}
                        className={`form-input ${errors.totp ? 'border-danger-500' : ''}`}
                        placeholder="000000 ou XXXX-XXXX"
                      />
                      {errors.totp ? (
                        <p className="form-error mt-1.5 flex items-center gap-1.5">
                          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden />
                          {errors.totp}
                        </p>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <label className="flex cursor-pointer items-center gap-2 select-none">
                      <input
                        id="remember-me"
                        name="remember-me"
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700">{t('rememberUser')}</span>
                    </label>
                    {isPublicConsultaEnabled(tenant) ? (
                      <Link
                        to="/process-lookup"
                        className="text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline"
                      >
                        Consultar andamento
                      </Link>
                    ) : null}
                    {isPublicDenunciaEnabled(tenant) ? (
                      <Link
                        to="/public-complaint"
                        className="text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline"
                      >
                        Denúncia anônima
                      </Link>
                    ) : null}
                    <Link
                      to="/forgot-password"
                      className="text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline"
                    >
                      {t('forgotPassword')}
                    </Link>
                    <a
                      href="/docs/"
                      className="text-sm font-medium text-primary-700 hover:text-primary-800 hover:underline"
                    >
                      Manual
                    </a>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="btn btn-primary btn-lg group mt-2 w-full gap-2 shadow-md shadow-primary-900/10 transition-all hover:shadow-lg disabled:opacity-70"
                  >
                    {loading ? (
                      <>
                        <span className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        {t('signingIn')}
                      </>
                    ) : (
                      <>
                        {t('signIn')}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
                      </>
                    )}
                  </button>
                </form>

                <div className="mt-8">
                  <div className="border-t border-gray-200" aria-hidden />

                  <div className="mt-5 space-y-3">
                    {allowOrgSignup ? (
                      <>
                        <Link
                          to="/register-organization"
                          className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary-200 bg-primary-50/80 px-4 py-3 text-sm font-semibold text-primary-900 transition-colors hover:bg-primary-100"
                        >
                          {t('registerOrg')}
                        </Link>
                        <p className="rounded-lg bg-gray-50 px-3 py-2 text-center text-xs leading-relaxed text-gray-600">
                          {t('firstAccessNote')}
                        </p>
                      </>
                    ) : (
                      <>
                        <Link
                          to="/primeiro-acesso"
                          className="flex w-full items-center justify-center gap-2 rounded-xl border border-primary-200 bg-primary-50/80 px-4 py-3 text-sm font-semibold text-primary-900 transition-colors hover:bg-primary-100"
                        >
                          {t('firstAccess')}
                        </Link>
                        <p className="rounded-lg bg-gray-50 px-3 py-2 text-center text-xs leading-relaxed text-gray-600">
                          {t('requerenteSignupLead', {
                            defaultValue: 'Crie seu acesso para cadastrar empreendimentos e acompanhar processos e licenças.',
                          })}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <p className="mt-8 text-center text-xs leading-relaxed text-slate-500">
              {t('footerSecure')}
            </p>
            <div className="mt-4 flex justify-center">
              <LanguageSwitcher variant="inline" />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Login;
